// ABOUTME: Filter bar for ShDataGrid3 — search input, filter modal trigger, column settings button.
// ABOUTME: Mirrors GridFilter2's shape but wires up the new GridcolumnSettings3 that supports left + right pinning.

import { Box, InputBase, useMediaQuery, useTheme } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import { FilterList } from "@mui/icons-material";

import { tokens } from "../../../theme";
import Shbutton from "../buttons/Shbutton";
import { useGridFilter } from "../../../contexts/GridFilterContext";
import { updateSearch } from "../../../newapi/global/filter/gridFilterSlice";
import FilterModal from "../datagrid2/Filter/FilterModal";

import GridcolumnSettings3 from "./GridcolumnSettings3";

const SearchComp = () => {
    const { t } = useTranslation();
    const { index, id } = useGridFilter();
    const dispatch = useDispatch();

    const reduxSearchTerm = useSelector(
        (state) => state.gridFilter.viewInstances[index]?.[id]?.searchString || ""
    );
    const isLargeScreen = useMediaQuery("(min-width:47.9375em)");

    const [localSearchTerm, setLocalSearchTerm] = useState(reduxSearchTerm);

    // Typing only updates local state — the dispatch to gridFilterSlice only
    // happens when the user hits Enter. Matches GridFilter2's behavior (search
    // is a commit action, not a debounced live filter).
    const handleSearchChange = (event) => {
        setLocalSearchTerm(event.target.value);
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            const value = e.target.value.trim();
            setLocalSearchTerm(value);
            dispatch(
                updateSearch({
                    index,
                    id,
                    searchString: value,
                })
            );
        }
    };

    useEffect(() => {
        setLocalSearchTerm(reduxSearchTerm);
    }, [reduxSearchTerm]);

    return (
        <InputBase
            autoFocus={isLargeScreen}
            sx={{
                maxWidth: 300,
                height: 40,
                paddingX: 3,
                flex: 1,
                width: 1,
            }}
            type="search"
            placeholder={t("search")}
            value={localSearchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
        />
    );
};

const GridFilter3 = memo((props) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [windowSize, setWindowSize] = useState(window.innerWidth);
    useEffect(() => {
        const onResize = () => setWindowSize(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const [columnSettingsAnchorEl, setColumnSettingsAnchorEl] = useState(null);
    const columnSettingsOpen = Boolean(columnSettingsAnchorEl);
    const columnSettingsPopoverId = columnSettingsOpen ? "grid3-column-settings-popover" : undefined;

    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const filterOpen = Boolean(filterAnchorEl);
    const filterPopoverId = filterOpen ? "grid3-filter-popover" : undefined;

    const handleColumnSettingsClick = (event) => setColumnSettingsAnchorEl(event.currentTarget);
    const handleColumnSettingsClose = () => setColumnSettingsAnchorEl(null);

    const handleFilterClick = (event) => setFilterAnchorEl(event.currentTarget);
    const handleFilterClose = () => setFilterAnchorEl(null);

    return (
        <Box
            className="grid-filter"
            display="flex"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
        >
            {!props.gridOptions?.hideSearch && (
                <Box
                    sx={{
                        background: `${
                            theme.palette.mode === "dark"
                                ? colors.grey[300]
                                : props.gridOptions?.isSidepanel && windowSize < 768
                                    ? colors.grey[200]
                                    : colors.grey[100]
                        }`,
                        display: "flex",
                        borderRadius: 5,
                        alignItems: "center",
                        paddingRight: 0.5,
                        "@media screen and (max-width: 47.9375em)": {
                            flex: 1,
                        },
                    }}
                >
                    <SearchComp />
                    <Shbutton
                        size="32"
                        color="primaryGrey"
                        variant="contained"
                        isFilterModalBtn
                        aria-describedby={columnSettingsPopoverId}
                        onClick={handleColumnSettingsClick}
                    >
                        <SettingsOutlined />
                    </Shbutton>
                </Box>
            )}

            {props.gridOptions?.filter?.length > 0 && (
                <Box>
                    <Shbutton
                        color={theme.palette.mode === "dark" ? "primaryGrey" : "secondaryGrey"}
                        variant="contained"
                        aria-describedby={filterPopoverId}
                        onClick={handleFilterClick}
                        sx={{
                            "& .shbtn-content": {
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                            },
                            "@media screen and (max-width: 47.9375em)": {
                                background:
                                    theme.palette.mode === "dark" ? colors.grey[500] : colors.grey[200],
                                px: "unset",
                                minWidth: "unset",
                                width: 40,
                            },
                        }}
                    >
                        <FilterList />
                    </Shbutton>
                </Box>
            )}

            <GridcolumnSettings3
                id={columnSettingsPopoverId}
                open={columnSettingsOpen}
                anchorEl={columnSettingsAnchorEl}
                onClose={handleColumnSettingsClose}
                columns={props.columns}
                onSaveAsNew={props.onSaveAsNew}
                onUpdateView={props.onUpdateView}
                hasActiveConfig={props.hasActiveConfig}
                disableViewSaving={props.disableViewSaving}
            />

            {props.gridOptions?.filter?.length > 0 && (
                <FilterModal
                    id={filterPopoverId}
                    open={filterOpen}
                    anchorEl={filterAnchorEl}
                    onClose={handleFilterClose}
                    filterOptions={props.gridOptions.filter}
                />
            )}

            {props.gridOptions?.disableCustomActionsOnPhone && windowSize > 767
                ? props.gridActions
                : null}
        </Box>
    );
});

export default GridFilter3;
